import { Injectable } from '@angular/core';
import { Field } from '@sitecore-jss/sitecore-jss/layout';
import { LinkField } from '@sitecore-jss/sitecore-jss-angular';

interface FormInputField {
  helpText: Field;
  label: Field;
  placeholder: string | null;
  alternativePlaceholder: string | null;
  link: LinkField | null;
  defaultValue: string | null;
  title: Field | null;
  values: Array<string>;
  otherCountry: string;
  shortListCountries: string;
  maxLength: string;
  mask: string;
}

interface FormErrorFields {
  description: string | null;
  message: string;
  type: string;
  parameter: string;
}

interface FormFields {
  fields: {
    [fieldName: string]: FormInputField;
  } | null;
  genericErrors: {
    [ErrorName: string]: FormErrorFields;
  } | null;
}

@Injectable({
  providedIn: 'root'
})
export class FormFieldsGeneratorService {
  constructor() {}

  private fields: FormFields;

  generateFormFields(rendering): {} {
    this.fields = {
      fields: {},
      genericErrors: {}
    };
    if (rendering.fields.items) {
      this.generateErrors(rendering.fields.items);
      this.generateFields(rendering.fields.items);
      return this.fields;
    } else {
      return {
        fields: null,
        genericErrors: null
      };
    }
  }

  private generateErrors(items) {
    let errorArray;
    // Isolate the array that holds errors;
    items.forEach(item => {
      if (item.name === 'Errors Folder') {
        errorArray = item.fields.items;
      }
    });
    //  Take previous generated array and create an object with JS readable names
    if (errorArray && errorArray.length > 0) {
      errorArray.forEach(error => {
        /**
         * error code fields are returned as :
         * {value: 'Validation.Field.Required'}
         * {value: 'Validation.Field.EmailAlreadyInUse'}
         *
         * so we strip the stuff we do not need and create a morelegible version by removing the dots:
         * `EmailAlreadyInUse`
         *
         * Assign this stripped string as a dynamic key to the error objects
         * {[errorKey]:value}
         * {EmailAlreadyInUse: 'this email is alreay in use'}
         * */
        const errorKey = (error as any).fields.code.value
          .replace('Validation.Field.', '')
          .replace('.', '');

        const camelCasedErrorKey = `${errorKey
          .charAt(0)
          .toLowerCase()}${errorKey.substr(1)}`;

        this.fields.genericErrors = Object.assign(this.fields.genericErrors, {
          [camelCasedErrorKey]: {
            description: error.fields.description
              ? error.fields.description.value
              : null,
            parameter: error.fields.parameter
              ? error.fields.parameter.value
              : null,
            message: error.fields.parameter
              ? error.fields.message.value.replace(
                  '%parameter%',
                  error.fields.parameter.value
                )
              : error.fields.message.value,
            type: error.fields.type.value
          }
        });
      });
    } else {
      this.fields.genericErrors = null;
    }
  }
  private generateFields(items) {
    let fieldArray;
    // Isolate the array that holds form fields;
    items.forEach(item => {
      if (item.name === 'Fields') {
        fieldArray = item.fields.items;
      }
    });

    if (fieldArray && fieldArray.length > 0) {
      //  Take previous generated array and create an object with JS readable names and easy structure
      fieldArray.forEach(field => {
        const fieldKey = (field as any).name.replace(/[ ]/g, '');
        const camelCasedFieldKey = `${fieldKey
          .charAt(0)
          .toLowerCase()}${fieldKey.substr(1)}`;
        this.fields.fields = Object.assign(this.fields.fields, {
          [camelCasedFieldKey]: {
            helpText: field.fields.helpText,
            label: field.fields.label,
            placeholder: field.fields.placeholder
              ? field.fields.placeholder.value
              : null,
            alternativePlaceholder: field.fields['Alternative Placeholder']
              ? field.fields['Alternative Placeholder']?.value
              : null,
            link: field.fields.Link ? field.fields.Link : null,
            defaultValue: field.fields.defaultValue
              ? field.fields.defaultValue.value
              : null,
            title: field.fields.title ? field.fields.title : null,
            values: field.fields.values ? field.fields.values : null,
            otherCountry: field.fields.otherCountry
              ? field.fields.otherCountry
              : null,
            shortListCountries: field.fields.shortListCountries
              ? field.fields.shortListCountries
              : null,
            maxLength: field.fields.maxLength
              ? field.fields.maxLength.value
              : null,
            mask: field.fields.mask ? field.fields.mask.value : null
          }
        });
      });
    } else {
      this.fields.fields = null;
    }
  }
}

export { FormInputField, FormErrorFields, FormFields };
